<template>
  <div class="d-flex flex-column gap-3">
    <div class="d-flex justify-content-between align-items-center p-2 bg-light rounded border">
      <div>
        <h5 class="mb-0">Apteki online</h5>
        <small class="text-muted">Lista aptek aktualnie połączonych do centrali</small>
      </div>
    </div>

    <CommonTable2
        :key="refreshTable"
        :table="tableConfig"
        rowIdentifier="drugstoreId"
        :currentPage="currentPage"
        :totalRows="totalRows"
        :totalPages="totalPages"
        :showPaginationTop="true"
        :showPaginationBottom="true"
        :showMassActions="true"
        :multiselect="true"
        :showRowActions="true"
        @onPageChange="handlePageChange"
        @onSort="handleSort"
        @onItemAction="handleItemAction"
        @onMassAction="handleMassAction"
        @onCheckItem="handleCheckItem"
        :sortColumn="sortColumn"
        :sortDirection="sortingDirection"
        :enableRowSelection="false"
    />

    <!-- MODAL: wysyłka komend -->
    <b-modal
        v-model="commandsModalVisible"
        title="Wyślij komendę"
        size="lg"
        hide-footer
        centered
    >
      <div class="d-flex flex-column gap-3">

        <div class="alert alert-warning d-flex align-items-center mb-0" role="alert">
          <i class="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Uwaga!</strong> Wykonywanie komend jest przeznaczone wyłącznie dla administratorów.
            Przed użyciem należy skonsultować się z administratorem systemu.
          </div>
        </div>

        <div class="p-2 bg-light border rounded">
          <div class="fw-semibold">Wybrane apteki:</div>
          <div class="text-muted small">
            <span v-if="commandsTargetDrugstoreIds.length === 0">Brak</span>
            <span v-else>
              {{ commandsTargetDrugstoreIds.join(", ") }}
            </span>
          </div>
        </div>

        <div>
          <label class="form-label fw-semibold mb-1">Komenda</label>
          <b-form-select
              v-model="selectedCommandName"
              :options="commandOptions"
              @change="handleCommandChange"
          />
          <div class="text-muted small mt-2" v-if="selectedCommandName">
            {{ getCommandDescription(selectedCommandName) }}
          </div>

          <div class="text-muted small mt-2" v-if="selectedCommandName && hasArgsDefinition(selectedCommandName)">
            <i class="fas fa-sliders-h me-1"></i>
            Ta komenda obsługuje argumenty (zdefiniowane lub dowolne).
            <span v-if="commandsArgsPreview && Object.keys(commandsArgsPreview).length > 0">
              Aktualnie ustawione: <span class="fw-semibold">{{ Object.keys(commandsArgsPreview).length }}</span>
            </span>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="outline-secondary" @click="commandsModalVisible = false" :disabled="sendingCommand">
            Anuluj
          </b-button>
          <b-button variant="primary" @click="sendCommand" :disabled="sendingCommand || !canSendCommand">
            <i class="fas fa-paper-plane me-1"></i>
            Wyślij komendę
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- MODAL: argumenty komendy -->
    <b-modal
        v-model="commandArgsModalVisible"
        title="Argumenty komendy"
        size="lg"
        hide-footer
        centered
        @hidden="onCommandArgsModalHidden"
    >
      <div class="d-flex flex-column gap-3">
        <div class="p-2 bg-light border rounded">
          <div class="fw-semibold">
            Komenda:
            <span class="ms-1">{{ getCommandLabel(selectedCommandName) }}</span>
          </div>
          <div class="text-muted small mt-1" v-if="selectedCommandName">
            {{ getCommandDescription(selectedCommandName) }}
          </div>
        </div>

        <div class="p-2 border rounded" v-if="argsAllowFreeForm || argsHasPredefined">
          <div class="fw-semibold mb-2">Dodaj argumenty</div>

          <div class="d-flex flex-wrap gap-3">
            <b-form-radio
                v-if="argsAllowFreeForm"
                v-model="commandArgsMode"
                value="FREE"
                name="command-args-mode"
            >
              Dowolne (klucz + wartość)
            </b-form-radio>

            <b-form-radio
                v-if="argsHasPredefined"
                v-model="commandArgsMode"
                value="PREDEFINED"
                name="command-args-mode"
            >
              Predefiniowane (z list)
            </b-form-radio>
          </div>

          <!-- FREE FORM ARGS -->
          <div v-if="commandArgsMode === 'FREE'" class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="text-muted small">
                Wpisz własne pary <span class="fw-semibold">klucz</span> i <span class="fw-semibold">wartość</span>.
              </div>
              <b-button size="sm" variant="outline-primary" @click="addFreeArgRow">
                <i class="fas fa-plus me-1"></i>Dodaj
              </b-button>
            </div>

            <div v-if="commandArgsFree.length === 0" class="text-muted small">
              Brak argumentów.
            </div>

            <div v-for="(row, idx) in commandArgsFree" :key="'free-' + idx" class="d-flex gap-2 mb-2">
              <b-form-input
                  v-model="row.key"
                  placeholder="Klucz"
              />
              <b-form-input
                  v-model="row.value"
                  placeholder="Wartość"
              />
              <b-button size="sm" variant="outline-danger" @click="removeFreeArgRow(idx)">
                <i class="fas fa-times"></i>
              </b-button>
            </div>
          </div>

          <!-- PREDEFINED ARGS -->
          <div v-if="commandArgsMode === 'PREDEFINED'" class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="text-muted small">
                Wybierz <span class="fw-semibold">klucz</span> i <span class="fw-semibold">wartość</span> z listy.
              </div>
              <b-button size="sm" variant="outline-primary" @click="addPredefinedArgRow">
                <i class="fas fa-plus me-1"></i>Dodaj
              </b-button>
            </div>

            <div v-if="commandArgsPredefined.length === 0" class="text-muted small">
              Brak argumentów.
            </div>

            <div v-for="(row, idx) in commandArgsPredefined" :key="'pre-' + idx" class="d-flex gap-2 mb-2">
              <b-form-select
                  v-model="row.key"
                  :options="getPredefinedKeyOptions()"
                  @change="onPredefinedKeyChanged(row)"
              />
              <b-form-select
                  v-model="row.value"
                  :options="getPredefinedValueOptions(row.key)"
                  :disabled="row.key == null"
              />
              <b-button size="sm" variant="outline-danger" @click="removePredefinedArgRow(idx)">
                <i class="fas fa-times"></i>
              </b-button>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="outline-secondary" @click="cancelCommandArgsModal">
            Anuluj
          </b-button>
          <b-button variant="primary" @click="saveCommandArgsModal">
            Zapisz argumenty
          </b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import {usePresenceAdminModule} from "@/state/pinia/presenceAdminModule";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";

export default {
  name: "PresenceListView",
  components: {CommonTable2},
  data() {
    return {
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      sortColumn: "lastSeenAt",
      sortingDirection: "DESC",
      refreshTable: true,

      // selection from table
      selectedItems: [],
      selectedItemsData: [],

      // modal state
      commandsModalVisible: false,
      commandsTargetDrugstoreIds: [],
      selectedCommandName: null,
      sendingCommand: false,

      // NEW: command args modal
      commandArgsModalVisible: false,
      commandArgsMode: "FREE", // FREE | PREDEFINED
      commandArgsFree: [],
      commandArgsPredefined: [],
      commandsArgsPreview: {}, // final args object for selected command

      // readable mapper for command select
      commandDefinitions: [
        {
          key: "SEND_DIAGNOSTICS",
          label: "Wyślij diagnostykę",
          description: "Zleca aptece wysłanie danych diagnostycznych do centrali.",
          group: "Administracja",
          priority: 1,
        },
        {
          key: "UPDATE_APP",
          label: "Aktualizuj aplikację",
          description: "Zleca aptece wykonanie procedury aktualizacji aplikacji.",
          group: "Administracja",
          priority: 2,
        },
        {
          key: "APP_SETTING_SYNC",
          label: "Ręczne uruchomienie pobierania ustawień centralnych",
          description: "Zleca aptece ręczne uruchomienie procedury pobierania ustawień centralnych. ",
          group: "Administracja",
          priority: 3,

        },

        {
          key: "RESET_SYNC_LOCAL_STATE",
          label: "Zresetuj stan synchronizacji lokalnej",
          description: "Powoduje , że dla wskazanego typu danych (np. towary, dostawcy, zamówienia), zostaną pobrane pełne dane w najbliżej sesji pobierania danych przez daną aptekę .",
          group: "Administracja",
          priority: 4,
          args: {
            allowFreeForm: true,
            predefined: [
              {
                key: "entityType",
                label: "Typ danych",
                values: ["PRODUCT", "PRODUCT_CATEGORY", "STOCK_SUPPLIER", "DRUGSTORE", "PACKAGE_ORDER"]
              }
            ],
          },
        },


        {
          key: "UPDATE_LICENSE",
          label: "Aktualizuj licencję",
          description: "Zleca aptece odświeżenie / pobranie aktualnej licencji.",
          group: "Administracja",
          priority: 5,
        },


        {
          key: "EXTEND_LOGGING_SEND_DIAGNOSTICS",
          label: "Włącz szczegółowe logowanie i wyślij diagnostykę",
          description: "Zleca aptece włączenie szczegółowego logowania (debug) na określony czas (np. 30 minut) oraz natychmiastowe wysłanie diagnostyki do centrali. Przydatne w sytuacjach, gdy potrzebujemy dodatkowych danych diagnostycznych z apteki.",
          group: "Administracja",
          priority: 6,
          args: {
            allowFreeForm: false,
            predefined: [
              {
                key: "timeout",
                label: "Czas na rozszerzonego zbieranie danych",
                values: ["5 min.", "10 min.", "30 min.", "1 h", "6 h", "12 h", "24 h"]
              }
            ],
          },


        },



        {
          key: "QUEUE_STATUS",
          label: "Sprawdź kolejkę zadań",
          description: "Zleca aptece sprawdzenie stanu kolejki zadań (diagnostyka ewentualnych zatorów).",
          group: "Administracja",
          priority: 7,
        },
        {
          key: "PROSPECTS_DIFFERENCES_UPLOAD",
          label: "Ręczne uruchomienie uploadu różnic prospektów",
          description: "Zleca aptece ręczne uruchomienie procedury uploadu różnic prospektów. ",
          group: "Zamówienia centralne",
          priority: 8,
        },
        {
          key: "PACKAGE_ORDERS_DOWNLOAD",
          label: "Ręczne uruchomienie pobierania zamówień pakietowych",
          description: "Zleca aptece ręczne uruchomienie procedury pobierania zamówień pakietowych oraz danych powiązanych (towary, dostawcy, pozycje). ",
          group: "Zamówienia centralne",
          priority: 9,
        },

        {
          key: "REPORTS_GENERATE_AND_SEND",
          label: "Ręczne uruchomienie generowania danych raportów centralnych",
          description: "Zleca aptece ręczne uruchomienie procedury generowania danych raportów centralnych oraz ich wysłania do centrali. Podanie parametru reportGuid umozliwi generowanie wyłącznie dla wskazanego raportu. Brak parametrów spowoduje generowanie wszystkich aktywnych raportów.",
          group: "Raporty",
          priority: 10,
          args: {
            allowFreeForm: true,
            predefined: [],
          },
        },
          {
          key: "REPORTS_EXPORT_FULL_DATA_AND_SEND",
          label: "Ręczne uruchomienie exportu pełnych danych danego raportu źródłowego",
          description: "Zleca aptece ręczne uruchomienie procedury exportu pełnych danych danego raportu źródłowego (np. sprzedaż, stany magazynowe) oraz ich wysłania do centrali. Podanie parametru reportGuid umożliwi generowanie wyłącznie dla wskazanego raportu. Brak parametrów spowoduje generowanie wszystkich aktywnych raportów.Opcjonalny argument fromDateTime (yyyy-MM-dd HH:mm:ss) pozwoli ograniczyć export danych do wskazanego przedziału czasowego np. tylko dane na rok wstecz",
          group: "Raporty",
          priority: 11,
          args: {
            allowFreeForm: true,
            predefined: [],
          },
        },

        {
          key: "PAYMENTS_DOWNLOAD_AND_APPLY",
          label: "Ręczne uruchomienie pobierania płatności centralnych",
          description: "Zleca aptece ręczne uruchomienie procedury pobierania płatności centralnych oraz ich zapisu w bazie Kamsoft.",
          group: "Płatności centralne",
          priority: 12,
        },

      ],

      tableConfig: {
        headers: [
          {key: "drugstoreId", text: "Numer apteki", type: "text", sortable: true, width: "160px"},
          {key: "machineName", text: "Nazwa komputera", type: "text", sortable: true, width: "100px"},
          {key: "appVersion", text: "Wersja aplikacji", type: "text", sortable: true, width: "100px"},
          {key: "lastSeenAt", text: "Ostatnia aktywność", type: "text", sortable: true, width: "200px"},
          {key: "online", text: "Online", type: "boolean", sortable: true, width: "100px"},
          {
            key: "actions", text: "Akcje", type: "actions", sortable: false, width: "120px",
            actions: [
              {
                enabled: true,
                title: "Komendy",
                class: "btn btn-sm btn-outline-primary d-inline-flex align-items-center",
                icon: "fas fa-terminal me-1",
                action: "open-command-modal",
                text: "",
              }
            ]

          },
        ],
        data: [],
        // NEW: mass actions
        massActions: [
          {
            text: "Komendy",
            icon: "fas fa-terminal",
            class: "btn btn-sm btn-outline-primary d-inline-flex align-items-center bg-white",
            enabled: true,
            action: "open-command-modal-bulk",
          },
        ],
      },
    };
  },
  computed: {
    presenceModule() {
      return usePresenceAdminModule();
    },
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonAppModule() {
      return useCommonAppModule();
    },
    commandOptions() {
      const options = [{value: null, text: "Wybierz komendę..."}];

      // Grupujemy komendy wg pola 'group'
      const grouped = this.commandDefinitions.reduce((acc, cmd) => {
        const group = cmd.group || "Inne";
        if (!acc[group]) acc[group] = [];
        acc[group].push(cmd);
        return acc;
      }, {});

      // Sortujemy nazwy grup (np. Administracja, Płatności centralne, Raporty, Zamówienia centralne)
      const groupNames = Object.keys(grouped).sort();

      groupNames.forEach(groupName => {
        // Dodajemy separator grupy
        options.push({
          value: null,
          text: `-- ${groupName.toUpperCase()} --`,
          disabled: true
        });

        // Sortujemy komendy wewnątrz grupy wg priorytetu i dodajemy do opcji
        const sortedCommands = grouped[groupName].sort((a, b) => (a.priority || 0) - (b.priority || 0));
        sortedCommands.forEach(cmd => {
          options.push({
            value: cmd.key,
            text: cmd.label
          });
        });
      });

      return options;
    },
    canSendCommand() {
      return (
          this.commandsTargetDrugstoreIds != null &&
          this.commandsTargetDrugstoreIds.length > 0 &&
          this.selectedCommandName != null &&
          ("" + this.selectedCommandName).length > 0
      );
    },

    // NEW: helper flags for args UI
    argsDefinition() {
      return this.getCommandArgsDefinition(this.selectedCommandName);
    },
    argsAllowFreeForm() {
      return this.argsDefinition?.allowFreeForm === true;
    },
    argsHasPredefined() {
      return Array.isArray(this.argsDefinition?.predefined) && this.argsDefinition.predefined.length > 0;
    },
  },
  methods: {
    // ---- CommonTable2 events ----
    handleCheckItem(payload) {
      this.selectedItems = payload?.items || [];
      this.selectedItemsData = payload?.itemsData || [];
    },
    handleItemAction(payload) {
      const action = payload?.action?.action;
      if (action === "open-command-modal") {
        const item = payload?.itemData;
        const drugstoreId = item?.drugstoreId;

        this.openCommandsModalForDrugstores(drugstoreId ? [drugstoreId] : []);
      }
    },
    handleMassAction(payload) {
      const action = payload?.action?.action;
      if (action === "open-command-modal-bulk") {
        const itemsData = payload?.itemsData || [];
        const ids = itemsData
            .map((x) => x?.drugstoreId)
            .filter((x) => x != null && ("" + x).trim().length > 0);

        // unique
        const uniqueIds = [...new Set(ids)];
        this.openCommandsModalForDrugstores(uniqueIds);
      }
    },

    // ---- modal helpers ----
    openCommandsModalForDrugstores(drugstoreIds) {
      this.commandsTargetDrugstoreIds = Array.isArray(drugstoreIds) ? drugstoreIds : [];
      this.selectedCommandName = null;

      // NEW: reset args state
      this.commandsArgsPreview = {};
      this.commandArgsFree = [];
      this.commandArgsPredefined = [];
      this.commandArgsMode = "FREE";

      this.commandsModalVisible = true;
    },
    getCommandDescription(commandKey) {
      const def = this.commandDefinitions.find((d) => d.key === commandKey);
      return def?.description || "";
    },
    getCommandLabel(commandKey) {
      const def = this.commandDefinitions.find((d) => d.key === commandKey);
      return def?.label || (commandKey || "");
    },
    getCommandGroup(commandKey) {
      const def = this.commandDefinitions.find((d) => d.key === commandKey);
      return def?.group || "";
    },
    getCommandPriority(commandKey) {
      const def = this.commandDefinitions.find((d) => d.key === commandKey);
      return def?.priority || 0;
    },

    // ---- args definitions ----
    getCommandArgsDefinition(commandKey) {
      if (!commandKey) return null;
      const def = this.commandDefinitions.find((d) => d.key === commandKey);
      return def?.args || null;
    },
    hasArgsDefinition(commandKey) {
      const argsDef = this.getCommandArgsDefinition(commandKey);
      const hasFree = argsDef?.allowFreeForm === true;
      const hasPre = Array.isArray(argsDef?.predefined) && argsDef.predefined.length > 0;
      return hasFree || hasPre;
    },

    // ---- command selection ----
    handleCommandChange() {
      if (!this.selectedCommandName) return;

      // reset previous args when switching command
      this.commandsArgsPreview = {};
      this.commandArgsFree = [];
      this.commandArgsPredefined = [];

      if (!this.hasArgsDefinition(this.selectedCommandName)) {
        // nothing else to do
        return;
      }

      // set default mode depending on definition
      if (this.argsAllowFreeForm) {
        this.commandArgsMode = "FREE";
      } else if (this.argsHasPredefined) {
        this.commandArgsMode = "PREDEFINED";
      } else {
        this.commandArgsMode = "FREE";
      }

      // ensure there is at least one row for the chosen mode (UX)
      if (this.commandArgsMode === "FREE") {
        this.addFreeArgRow();
      } else if (this.commandArgsMode === "PREDEFINED") {
        this.addPredefinedArgRow();
      }

      // open args modal (separate okienko)
      this.commandsModalVisible = false;
      this.commandArgsModalVisible = true;
    },
    onCommandArgsModalHidden() {
      // safety: if someone closes modal via X/ESC, go back to commands modal
      if (this.selectedCommandName != null) {
        this.commandsModalVisible = true;
      }
    },
    cancelCommandArgsModal() {
      // revert selection (user cancelled providing args)
      this.selectedCommandName = null;
      this.commandsArgsPreview = {};
      this.commandArgsFree = [];
      this.commandArgsPredefined = [];
      this.commandArgsModalVisible = false;
      this.commandsModalVisible = true;
    },
    saveCommandArgsModal() {
      // Build args preview object based on current rows
      const argsObj = {};

      // free-form args
      if (Array.isArray(this.commandArgsFree)) {
        this.commandArgsFree.forEach((row) => {
          const key = (row?.key || "").trim();
          if (!key) return;
          argsObj[key] = (row?.value != null ? ("" + row.value) : "");
        });
      }

      // predefined args
      if (Array.isArray(this.commandArgsPredefined)) {
        this.commandArgsPredefined.forEach((row) => {
          const key = (row?.key || "").trim();
          if (!key) return;
          argsObj[key] = (row?.value != null ? ("" + row.value) : "");
        });
      }

      this.commandsArgsPreview = argsObj;

      this.commandArgsModalVisible = false;
      this.commandsModalVisible = true;
    },

    // ---- args modal actions ----
    addFreeArgRow() {
      this.commandArgsFree.push({key: "", value: ""});
    },
    removeFreeArgRow(idx) {
      this.commandArgsFree.splice(idx, 1);
    },

    addPredefinedArgRow() {
      this.commandArgsPredefined.push({key: null, value: null});
    },
    removePredefinedArgRow(idx) {
      this.commandArgsPredefined.splice(idx, 1);
    },
    getPredefinedKeyOptions() {
      const options = [{value: null, text: "Wybierz klucz..."}];
      const defs = this.argsDefinition?.predefined || [];
      defs.forEach((d) => {
        options.push({value: d.key, text: d.label || d.key});
      });
      return options;
    },
    getPredefinedValueOptions(key) {
      const options = [{value: null, text: "Wybierz wartość..."}];
      if (!key) return options;

      const defs = this.argsDefinition?.predefined || [];
      const def = defs.find((d) => d.key === key);
      const values = def?.values || [];

      values.forEach((v) => {
        if (v != null && typeof v === "object") {
          options.push({value: v.value, text: v.label || v.value});
        } else {
          options.push({value: v, text: "" + v});
        }
      });

      return options;
    },
    onPredefinedKeyChanged(row) {
      // reset value when key changes
      if (row) row.value = null;
    },

    // ---- sending command ----
    sendCommand() {
      if (!this.canSendCommand) return;

      this.sendingCommand = true;

      const payload = {
        drugstoreIds: this.commandsTargetDrugstoreIds,
        commandName: this.selectedCommandName,
        args: this.commandsArgsPreview || {},
      };

      // metoda ma być dopisana do commonAppModule.js: httpPostPresenceCommands(payload)
      this.commonAppModule
          .httpPostPresenceCommands(payload)
          .then(() => {
            // apiRequestsHelper.handleResponseWithDialogs(...) pokaże OK/Warning (np. 203)
            this.commandsModalVisible = false;
          })
          .finally(() => {
            this.sendingCommand = false;
          });
    },

    // ---- existing logic (nie ruszam poza koniecznym) ----
    handlePageChange(page) {
      this.currentPage = page;
      this.refreshPresenceList();
    },
    handleSort(sortingKeyValue) {
      if (this.sortColumn === sortingKeyValue) {
        this.sortingDirection = this.sortingDirection === "ASC" ? "DESC" : "ASC";
      } else {
        this.sortColumn = sortingKeyValue;
        this.sortingDirection = "DESC";
      }
      this.currentPage = 1;
      this.refreshPresenceList();
    },
    refreshPresenceList() {
      this.refreshTable = false;
      this.presenceModule
          .httpGetPresencePaging({
            currentPage: this.currentPage,
            sortingKey: this.sortColumn,
            sortingDirection: this.sortingDirection,
          })
          .then(() => {
            this.totalPages = this.presenceModule.getPresencePagesCount || 1;
            this.totalRows = this.presenceModule.getPresenceDataTotal || 0;

            this.tableConfig.data = [...this.presenceModule.getPresence];
            this.tableConfig.data.forEach((item) => {
              item['lastSeenAt'] = item['lastSeenAt'] != null ? new Date(Math.floor(item['lastSeenAt'] * 1000)) : new Date(0);
            });

            this.refreshTable = true;
          });
    },
  },
  mounted() {
    this.simplepharmAuth.refreshToken().then(() => {
      this.refreshPresenceList();
    });
  },
};
</script>